// OutboundEditRuleModal.jsx

import React from 'react';

// Utilities
import { cleanString } from '../../../apiUtils';
import { normalizeRuleEvent } from '../../utils/integrationUtils';

// Constants
import { RULE_EVENT_OPTIONS } from '../../constants/integrationOptions';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import ModalActions from './ModalActions';

/**
 * OutboundEditRuleModal
 *
 * Renders the modal used to edit an existing outbound integration rule.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {boolean} canManageCredentials - Whether the user can manage outbound rules.
 * @param {string} busyActionKey - Current busy action key used to disable actions.
 * @param {object} editingRule - Rule currently being edited.
 * @param {string} editDraftName - Draft rule name.
 * @param {string} editDraftEvent - Draft trigger event.
 * @param {string} editDraftTypeId - Draft type id.
 * @param {string} editDraftStatus - Draft rule status.
 * @param {string} editDraftDestinationUrl - Draft destination url.
 * @param {string} editDraftDestinationName - Draft destination name.
 * @param {array} typeRows - Available type options.
 * @param {function} onEditDraftNameChange - Handler to update draft name.
 * @param {function} onEditDraftEventChange - Handler to update draft event.
 * @param {function} onEditDraftTypeIdChange - Handler to update draft type id.
 * @param {function} onEditDraftStatusChange - Handler to update draft status.
 * @param {function} onEditDraftDestinationUrlChange - Handler to update draft destination url.
 * @param {function} onEditDraftDestinationNameChange - Handler to update draft destination name.
 * @param {function} onSave - Handler to save rule edits.
 *
 * @returns {ReactNode} The rendered edit outbound rule modal.
 */
const OutboundEditRuleModal = ({
    theme,
    isOpen,
    onClose,
    canManageCredentials,
    busyActionKey,
    editingRule,
    editDraftName,
    editDraftEvent,
    editDraftTypeId,
    editDraftStatus,
    editDraftDestinationUrl,
    editDraftDestinationName,
    typeRows,
    onEditDraftNameChange,
    onEditDraftEventChange,
    onEditDraftTypeIdChange,
    onEditDraftStatusChange,
    onEditDraftDestinationUrlChange,
    onEditDraftDestinationNameChange,
    onSave,
}) => {

    // ----- DERIVED VALUES ----- //

    const editingRuleId = cleanString(editingRule?.id || editingRule?.key);


    // ----- RENDER ----- //

    return (
        <Modal
            title="Edit Outbound Rule"
            isOpen={isOpen}
            onClose={onClose}
            width="620px"
            maxHeight="85vh"
        >
            <div className="api-integrations-modal-body">
                {/* INTRO */}
                <div
                    className="api-integrations-subtitle"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Update rule settings and destination details.
                </div>

                {/* FORM */}
                <div className="api-integrations-form-grid">
                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Rule name
                    </div>
                    <TextInput
                        value={editDraftName}
                        onChange={(event) => onEditDraftNameChange(event.target.value)}
                        placeholder="Example: Tasks -> Zapier Sheet"
                        disabled={!canManageCredentials}
                    />

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Trigger event
                    </div>
                    <SelectInput
                        value={editDraftEvent}
                        onChange={(event) => onEditDraftEventChange(normalizeRuleEvent(event.target.value))}
                        disabled={!canManageCredentials}
                    >
                        {RULE_EVENT_OPTIONS.map((row) => (
                            <option key={row.value} value={row.value}>{row.label}</option>
                        ))}
                    </SelectInput>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Trigger type
                    </div>
                    <SelectInput
                        value={editDraftTypeId}
                        onChange={(event) => onEditDraftTypeIdChange(cleanString(event.target.value))}
                        disabled={!canManageCredentials || typeRows.length === 0}
                    >
                        {typeRows.map((row) => (
                            <option key={row.typeId} value={row.typeId}>{row.label}</option>
                        ))}
                    </SelectInput>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Rule status
                    </div>
                    <SelectInput
                        value={editDraftStatus}
                        onChange={(event) => onEditDraftStatusChange(cleanString(event.target.value).toLowerCase() === 'paused' ? 'paused' : 'active')}
                        disabled={!canManageCredentials}
                    >
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                    </SelectInput>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Destination URL
                    </div>
                    <TextInput
                        value={editDraftDestinationUrl}
                        onChange={(event) => onEditDraftDestinationUrlChange(event.target.value)}
                        placeholder="https://hooks.zapier.com/hooks/catch/..."
                        disabled={!canManageCredentials}
                    />

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Destination name (optional)
                    </div>
                    <TextInput
                        value={editDraftDestinationName}
                        onChange={(event) => onEditDraftDestinationNameChange(event.target.value)}
                        placeholder="Zapier Catch Hook"
                        disabled={!canManageCredentials}
                    />
                </div>

                {/* ACTIONS */}
                <ModalActions>
                    <Button
                        label="Cancel"
                        onClick={onClose}
                        enabled={busyActionKey !== `edit:${editingRuleId}`}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Save Changes"
                        onClick={onSave}
                        enabled={canManageCredentials && busyActionKey !== `edit:${editingRuleId}`}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                </ModalActions>
            </div>
        </Modal>
    );
};

// Dev Tools
OutboundEditRuleModal.displayName = 'OutboundEditRuleModal';

export default OutboundEditRuleModal;
